import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {  getInitialLang } from '../Common'
import { checkIfHasInvestorAccess } from './support/checkIfHasInvestorAccess'

import { $MAX_WIDTH } from '../Consts'
import FeatureTiles from './support/FeatureTiles'

const NAVBAR_HEIGHT = 56
const accentFrom = '#1D4ED8'
const accentTo = '#0EA5E9'
const buttonFrom = '#4F8DF9'
const buttonTo = '#3063F0'
const buttonHoverFrom = '#69A1FF'
const buttonHoverTo = '#4877F6'

const EN = {
  heading: 'Welcome, Investors',
  intro:
    'BusinessA stands at a pivotal moment with a functional prototype in hand. We are seeking strategic funding to accelerate product hardening and go-to-market, and equally important, a partner whose experience and network can propel us into enterprise channels.',
  demo: 'Demo Video',
  deck: 'Pitch Deck',
  plan: 'Business Plan',
}
const ZH = {
  heading: '欢迎，投资人',
  intro:
    'BusinessA 已拥有可用原型，并正处于关键时刻。我们正在寻求战略融资，以加速产品打磨和市场推广，同时希望找到能够凭借经验与网络将我们推向企业渠道的合作伙伴。',
  demo: '演示视频',
  deck: '商业计划书',
  plan: '商业计划',
}
const LANGS = { en: EN, zh: ZH }

const InvestorPage: React.FC = () => {
  const loc = useLocation()
  const [lang] = useState<'en' | 'zh'>(getInitialLang)
  const t = LANGS[lang]
  const toggleUrl = (l: 'en' | 'zh') =>
    `${loc.pathname}${l === 'en' ? '' : '?lang=zh'}`

  useEffect(() => {
    if (checkIfHasInvestorAccess()) return

    const authUrl = new URL('/auth.html', window.location.origin)
    authUrl.searchParams.set('url', window.location.href)

    window.location.replace(authUrl.href)
  }, [])

  const pageStyle: React.CSSProperties = {
    width: '100%',
    minHeight: '100vh',
    boxSizing: 'border-box',
    color: '#09213D',
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: `${NAVBAR_HEIGHT + 64}px 1rem 4rem`,
  }

  const wrapStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: $MAX_WIDTH,
    textAlign: 'center',
    boxSizing: 'border-box',
  }

  const headingStyle: React.CSSProperties = {
    fontSize: '2.9rem',
    fontWeight: 800,
    marginBottom: '1.6rem',
    lineHeight: 1.12,
  }

  const gradientText: React.CSSProperties = {
    backgroundImage: `linear-gradient(90deg, ${accentFrom}, ${accentTo})`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
  }

  const introStyle: React.CSSProperties = {
    fontSize: '1.15rem',
    lineHeight: 1.75,
    marginBottom: '3.4rem',
  }

  const buttonRow: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '1.4rem',
    width: '100%',
    maxWidth: $MAX_WIDTH,
    marginBottom: '4rem',
    marginTop: 10,
  }

  const ctaBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 14,
    padding: '1.15rem 2.2rem',
    borderRadius: 18,
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#FFFFFF',
    textDecoration: 'none',
    backgroundImage: `linear-gradient(135deg, ${buttonFrom}, ${buttonTo})`,
    boxShadow: '0 10px 26px rgba(49,102,242,0.35)',
    transition: 'all .25s ease',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  }

  const ctaHover: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, ${buttonHoverFrom}, ${buttonHoverTo})`,
    transform: 'translateY(-4px) scale(1.015)',
    boxShadow: '0 14px 36px rgba(49,102,242,0.5)',
  }

  const iconStyle: React.CSSProperties = {
    width: 26,
    height: 26,
    fill: 'currentColor',
    flexShrink: 0,
  }

  const CTAButton: React.FC<{
    to: string
    label: string
    svgPath: string
  }> = ({ to, label, svgPath }) => {
    const [hover, setHover] = React.useState(false)
    return (
      <Link
        to={to}
        style={{
          ...ctaBase,
          ...(hover ? ctaHover : {}),
          display: 'inline-flex',   // make the <Link> a flex row
          alignItems: 'center',     // vertically center its children
          gap: '0.4em'              // optional spacing icon ↔ text
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <svg
          viewBox="0 0 26 26"
          style={{ width: 26, height: 26, fill: 'currentColor', flexShrink: 0 }}
        >
          <path d={svgPath} />
        </svg>
        {label}
      </Link>
    )
  }

  const langToggleBase: React.CSSProperties = {
    position: 'absolute',
    top: NAVBAR_HEIGHT + 20,
    right: 20,
    display: 'flex',
    gap: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
  }
  const langLink: React.CSSProperties = {
    color: lang === 'en' ? (lang === 'en' ? accentFrom : '#666') : accentFrom,
    textDecoration: 'none',
  }

  return (
    <div style={pageStyle}>
      <div style={wrapStyle}>
        <h1 style={headingStyle}>
          <span style={gradientText}>{t.heading}</span>
        </h1>
        <p style={introStyle}>{t.intro}</p>
      </div>

      <div style={buttonRow}>
        <CTAButton to="/investor/demo" label={t.demo} svgPath="M8 5v14l11-7L8 5z" />
        <CTAButton
          to="/investor/pitch-deck"
          label={t.deck}
          svgPath="M3 8.25C3 7.56 3.56 7 4.25 7h15.5c.69 0 1.25.56 1.25 1.25v11.5c0 .69-.56 1.25-1.25 1.25H15l-3 3-3-3H4.25C3.56 21 3 20.44 3 19.75V8.25zM8 13v4M12 14.5v2.5M16 12v5"
        />
        <CTAButton
          to="/investor/business-plan"
          label={t.plan}
          svgPath="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4c0-1.1.9-2 2-2zm8 1.5V8h4.5L14 3.5z"
        />
      </div>

      <div style={{ ...wrapStyle, marginTop: 20 }}>
        <FeatureTiles />
      </div>
    </div>
  )
}

export default InvestorPage
